import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getMyProfile, updateMyProfile } from '../../api';
import useUserStore from '../../hooks/useUserStore';
import ResponsiveContainer from '../../common/ResponsiveContainer';
import { toastConfig } from '../../config';

const SpinnerIcon = () => (
  <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
  </svg>
);

const StatCard = ({ icon, label, value }) => (
  <div className='flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-xl p-4'>
    <div className='flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-1 shrink-0'>
      {icon}
    </div>
    <div>
      <p className='text-xs text-gray-400 font-medium uppercase tracking-wide'>{label}</p>
      <p className='text-lg font-bold text-gray-800'>{value}</p>
    </div>
  </div>
);

const roleLabel = {
  admin: 'Administrador',
  donor: 'Donador',
  user: 'Usuario',
};

const Profile = () => {
  const storeUser = useUserStore((state) => state.user);
  const updateUserData = useUserStore((state) => state.updateUserData);

  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [alternativeEmail, setAlternativeEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMyProfile()
      .then((res) => {
        setProfile(res.data);
        setName(res.data.name);
        setLastname(res.data.lastname);
        setAlternativeEmail(res.data.alternativeEmail || '');
      })
      .catch(() => {
        // Fallback to store data if endpoint not yet available
        if (storeUser) {
          setProfile(storeUser);
          setName(storeUser.name || '');
          setLastname(storeUser.lastname || '');
          setAlternativeEmail(storeUser.alternativeEmail || '');
        }
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMyProfile({ name: name.trim(), lastname: lastname.trim(), alternativeEmail: alternativeEmail.trim() });
      updateUserData({ name: name.trim(), lastname: lastname.trim(), alternativeEmail: alternativeEmail.trim() });
      setProfile((p) => ({ ...p, name: name.trim(), lastname: lastname.trim(), alternativeEmail: alternativeEmail.trim() }));
      toast.success('Datos actualizados', toastConfig);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar', toastConfig);
    } finally {
      setSaving(false);
    }
  };

  const initials = profile
    ? `${profile.name?.[0] ?? ''}${profile.lastname?.[0] ?? ''}`.toUpperCase()
    : '?';

  const chaptersWatched = profile?.views?.length ?? 0;
  const registeredAt = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  return (
    <ResponsiveContainer className='font-montserrat mt-6 md:px-5 pb-12'>
      {!profile ? (
        <div className='flex justify-center mt-20'>
          <span className='loader2' />
        </div>
      ) : (
        <div className='flex flex-col gap-5'>

          {/* Header card */}
          <div className='bg-white rounded-xl shadow p-6 md:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-5'>
            {/* Avatar */}
            <div className='flex items-center justify-center w-20 h-20 rounded-full bg-red-1 text-white text-2xl font-bold shrink-0 select-none'>
              {initials}
            </div>
            <div className='flex flex-col items-center sm:items-start gap-1'>
              <h1 className='text-xl font-bold text-gray-800'>
                {profile.name} {profile.lastname}
              </h1>
              <p className='text-sm text-gray-500'>{profile.email}</p>
              <span className='mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700'>
                {roleLabel[profile.role] ?? profile.role}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <StatCard
              label='Capítulos vistos'
              value={chaptersWatched}
              icon={
                <svg className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                  <path strokeLinecap='round' strokeLinejoin='round' d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
                  <path strokeLinecap='round' strokeLinejoin='round' d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
              }
            />
            <StatCard
              label='Miembro desde'
              value={registeredAt}
              icon={
                <svg className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                  <path strokeLinecap='round' strokeLinejoin='round' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' />
                </svg>
              }
            />
          </div>

          {/* Edit form */}
          <div className='bg-white rounded-xl shadow p-6 md:p-8'>
            <div className='flex items-center justify-between mb-5 pb-1 border-b-2'>
              <h2 className='font-bold text-lg text-gray-800'>Información personal</h2>
              <button
                onClick={handleSave}
                disabled={saving}
                className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded hover:bg-red-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
              >
                {saving && <SpinnerIcon />}
                Guardar
              </button>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4'>
              {/* Name */}
              <div className='flex flex-col gap-1'>
                <label className='text-sm font-semibold text-red-1'>Nombre</label>
                <input
                  type='text'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className='p-2 outline-none border-2 rounded focus:border-red-2 transition-all text-gray-700'
                />
              </div>

              {/* Lastname */}
              <div className='flex flex-col gap-1'>
                <label className='text-sm font-semibold text-red-1'>Apellido</label>
                <input
                  type='text'
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className='p-2 outline-none border-2 rounded focus:border-red-2 transition-all text-gray-700'
                />
              </div>

              {/* Email — read only */}
              <div className='flex flex-col gap-1'>
                <label className='text-sm font-semibold text-gray-400'>Correo electrónico</label>
                <input
                  type='email'
                  value={profile.email}
                  disabled
                  className='p-2 border-2 rounded bg-gray-50 text-gray-400 cursor-not-allowed'
                />
                <p className='text-xs text-gray-400'>El correo no puede ser modificado.</p>
              </div>

              {/* Alternative email */}
              <div className='flex flex-col gap-1'>
                <label className='text-sm font-semibold text-red-1'>Correo alternativo</label>
                <input
                  type='email'
                  value={alternativeEmail}
                  onChange={(e) => setAlternativeEmail(e.target.value)}
                  placeholder='Correo para recibir notificaciones'
                  className='p-2 outline-none border-2 rounded focus:border-red-2 transition-all text-gray-700'
                />
                <p className='text-xs text-gray-400'>Las notificaciones se enviarán a este correo si está configurado.</p>
              </div>
            </div>
          </div>

        </div>
      )}
    </ResponsiveContainer>
  );
};

export default Profile;
